import React, { useEffect, useState, useMemo, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import LogoDisplay from "@/components/LogoDisplay";
import { Input } from "@/components/ui/input";
import { Search, Info, CheckCircle2, AlertTriangle, ShieldAlert, X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";

const ANN_TYPES = {
  info: { Icon: Info, bg: "bg-sky-50 border-sky-200 text-sky-900 dark:bg-sky-950/40 dark:border-sky-900 dark:text-sky-100", iconClr: "text-sky-600" },
  success: { Icon: CheckCircle2, bg: "bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-100", iconClr: "text-emerald-600" },
  warning: { Icon: AlertTriangle, bg: "bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-100", iconClr: "text-amber-600" },
  danger: { Icon: ShieldAlert, bg: "bg-rose-50 border-rose-200 text-rose-900 dark:bg-rose-950/40 dark:border-rose-900 dark:text-rose-100", iconClr: "text-rose-600" },
};

export default function Home() {
  const navigate = useNavigate();
  const [data, setData] = useState({ links: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [site, setSite] = useState({ title: "NetKom Portal", subtitle: "Plateforme d'accès rapide", icons_per_row: 6, bg_image_path: "" });
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [activeSubCat, setActiveSubCat] = useState("all");
  const [announcements, setAnnouncements] = useState([]);
  const [annIdx, setAnnIdx] = useState(0);
  const [annClosed, setAnnClosed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: links }, { data: s }, { data: anns }] = await Promise.all([
          api.get("/public/links"),
          api.get("/public/site"),
          api.get("/public/announcements").catch(() => ({ data: [] })),
        ]);
        setData(links);
        setSite(s);
        setAnnouncements(anns || []);
      } catch (e) {
        if (e.response?.status === 403) {
          navigate("/access-denied", { replace: true });
          return;
        }
        setError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  // Auto-rotation des annonces (8s)
  useEffect(() => {
    if (announcements.length < 2) return;
    const t = setInterval(() => setAnnIdx((i) => (i + 1) % announcements.length), 8000);
    return () => clearInterval(t);
  }, [announcements.length]);

  // Map cat_id → parent_id pour la hiérarchie
  const catParent = useMemo(() => {
    const m = {};
    (data.categories || []).forEach((c) => { m[c.id] = c.parent_id || null; });
    return m;
  }, [data.categories]);

  const rootCats = useMemo(
    () => (data.categories || []).filter((c) => !c.parent_id),
    [data.categories]
  );

  const subCatsOf = useMemo(() => {
    const m = {};
    (data.categories || []).forEach((c) => {
      if (c.parent_id) (m[c.parent_id] = m[c.parent_id] || []).push(c);
    });
    return m;
  }, [data.categories]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.links.filter((l) => {
      // Quand "Tout" est actif : on masque les liens des sous-catégories
      // (ils apparaîtront uniquement quand l'utilisateur sélectionne leur catégorie parente)
      if (activeCat === "all" && l.category_id && catParent[l.category_id]) {
        return false;
      }
      if (activeCat !== "all") {
        const inCat = l.category_id === activeCat || catParent[l.category_id] === activeCat;
        if (!inCat) return false;
        if (activeSubCat !== "all" && l.category_id !== activeSubCat) return false;
      }
      if (!q) return true;
      return (
        l.title.toLowerCase().includes(q) ||
        (l.description || "").toLowerCase().includes(q) ||
        l.url.toLowerCase().includes(q)
      );
    });
  }, [data.links, query, activeCat, activeSubCat, catParent]);

  const grouped = useMemo(() => {
    // Toujours une grille plate — les filtres de catégorie remplacent les en-têtes
    return [{ id: "flat", name: null, links: filtered, color: null }];
  }, [filtered]);

  const handleClick = (link, e) => {
    // Copie credentials dans le presse-papier si présents
    if (link.has_credentials || link.cred_login || link.cred_password) {
      try {
        const text = [link.cred_login, link.cred_password].filter(Boolean).join("\t");
        if (text && navigator.clipboard) {
          navigator.clipboard.writeText(text).catch(() => {});
          toast.success(`Identifiants copiés (${link.cred_login || "—"})`, { duration: 3500 });
        }
      } catch {}
    }
    // Cas spécial RDP : Windows ne reconnaît pas rdp:// → on télécharge un .rdp
    if (/^rdp:/i.test(link.url)) {
      e?.preventDefault();
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/public/rdp/${link.id}`;
      return;
    }
    api.post(`/public/click/${link.id}`).catch(() => {});
    // Pour les autres schémas custom (ms-teams://, slack://, etc.) — pas de nouvel onglet
    if (!/^https?:/i.test(link.url)) {
      e?.preventDefault();
      window.location.href = link.url;
    }
  };

  const bgStyle = useMemo(() => site.bg_image_path ? {
    backgroundImage: `linear-gradient(180deg, hsla(0,0%,100%,0.86) 0%, hsla(0,0%,100%,0.96) 100%), url(${site.bg_image_path})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
  } : undefined, [site.bg_image_path]);

  // Construit le contenu d'annonce pour le slot central du header
  const announcementSlot = !annClosed && announcements.length > 0 ? (
    <div className="w-full max-w-xl" data-testid="announcements-banner">
      <div className="grid">
        {announcements.map((a, i) => {
          const meta = ANN_TYPES[a.type] || ANN_TYPES.info;
          const Icon = meta.Icon;
          const active = i === annIdx;
          return (
            <div
              key={a.id}
              className={`relative pl-10 pr-8 py-2.5 rounded-xl border ${meta.bg} shadow-sm transition-opacity duration-300`}
              style={{
                gridArea: "1 / 1",
                opacity: active ? 1 : 0,
                visibility: active ? "visible" : "hidden",
                pointerEvents: active ? "auto" : "none",
              }}
              data-testid={`announcement-${a.id}`}
            >
              <Icon className={`h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 ${meta.iconClr}`} />
              <div className="text-xs leading-snug whitespace-pre-wrap line-clamp-2">
                {a.link_url ? (
                  <a href={a.link_url} target="_blank" rel="noopener noreferrer" className="underline font-semibold">{a.message}</a>
                ) : (
                  a.message
                )}
              </div>
              <button
                onClick={() => setAnnClosed(true)}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/10"
                aria-label="Fermer"
                data-testid="announcement-close"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          );
        })}
      </div>
      {announcements.length > 1 && (
        <div className="flex justify-center gap-1 mt-1.5" data-testid="announcement-dots">
          {announcements.map((_, i) => (
            <button
              key={i}
              onClick={() => setAnnIdx(i)}
              className={`h-1 rounded-full transition-all ${i === annIdx ? "bg-primary w-5" : "bg-border w-1"}`}
              aria-label={`Annonce ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  ) : null;

  return (
    <TooltipProvider delayDuration={2000}>
    <div className="min-h-screen flex flex-col bg-background" style={bgStyle}>
      <Header siteTitle={site.title} siteLogo={site.site_logo_path || ""} siteLogoDark={site.site_logo_dark_path || ""} centerSlot={announcementSlot} />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-6">
        {/* Recherche + filtres centrés tout en haut */}
        <section className="mb-16 flex flex-col items-center gap-4" data-testid="home-search-row">
          <div className="relative w-full max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un lien, un outil, un site…"
              className="pl-10 h-11 rounded-full border-2 border-primary focus-visible:ring-4 focus-visible:ring-primary/25"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="home-search-input"
            />
          </div>
          {rootCats.length > 0 && (
            <div className="flex flex-col gap-2 items-center" data-testid="home-category-filters">
              {/* Découpe en lignes de 8 chips maximum */}
              {Array.from({ length: Math.ceil((rootCats.length + 1) / 8) }, (_, rowIdx) => {
                // On insère le bouton "Tout" en première position
                const all = rowIdx === 0 ? [{ id: "__all__", name: "Tout", _all: true }] : [];
                const start = rowIdx === 0 ? 0 : rowIdx * 8 - 1;
                const end = start + (8 - all.length);
                const rowItems = [...all, ...rootCats.slice(start, end)];
                return (
                  <div key={rowIdx} className="flex gap-2 flex-wrap justify-center">
                    {rowItems.map((c) =>
                      c._all ? (
                        <button
                          key="all"
                          onClick={() => { setActiveCat("all"); setActiveSubCat("all"); }}
                          className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider font-medium border transition-colors ${
                            activeCat === "all"
                              ? "bg-primary text-primary-foreground border-primary"
                              : "border-border bg-background hover:bg-accent"
                          }`}
                          data-testid="cat-filter-all"
                        >
                          Tout
                        </button>
                      ) : (
                        <button
                          key={c.id}
                          onClick={() => { setActiveCat(c.id); setActiveSubCat("all"); }}
                          className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider font-medium border transition-colors ${
                            activeCat === c.id
                              ? "text-white border-transparent"
                              : "border-border bg-background hover:bg-accent"
                          }`}
                          style={activeCat === c.id ? { backgroundColor: c.color || "#002FA7" } : undefined}
                          data-testid={`cat-filter-${c.id}`}
                        >
                          {c.name}
                        </button>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {activeCat !== "all" && (subCatsOf[activeCat] || []).length > 0 && (
            <div className="flex gap-1.5 flex-wrap justify-center mt-1" data-testid="home-subcategory-filters">
              <button
                onClick={() => setActiveSubCat("all")}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border-dashed border transition-colors ${
                  activeSubCat === "all"
                    ? "border-solid bg-primary/10 text-primary border-primary"
                    : "border-border bg-background hover:bg-accent text-muted-foreground"
                }`}
              >
                Tout
              </button>
              {subCatsOf[activeCat].map((sc) => (
                <button
                  key={sc.id}
                  onClick={() => setActiveSubCat(sc.id)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium border-dashed border transition-colors ${
                    activeSubCat === sc.id
                      ? "border-solid text-white"
                      : "border-border bg-background hover:bg-accent text-muted-foreground"
                  }`}
                  style={activeSubCat === sc.id ? { backgroundColor: sc.color || "#002FA7", borderColor: sc.color || "#002FA7" } : undefined}
                  data-testid={`subcat-filter-${sc.id}`}
                >
                  {sc.name}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Annonces déplacées dans le header (slot central) */}

        {loading && <p className="text-muted-foreground text-sm">Chargement des liens…</p>}
        {error && (
          <p className="text-destructive text-sm" data-testid="home-error">
            {error}
          </p>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="border border-dashed border-border rounded-sm p-12 text-center" data-testid="home-empty">
            <p className="text-muted-foreground text-sm">
              Aucun lien à afficher. Connectez-vous au backoffice pour en ajouter.
            </p>
          </div>
        )}

        <div className="space-y-12">
          {grouped.map((group) => {
            const ipr = Math.max(1, Math.min(12, site.icons_per_row || 6));
            return (
              <section key={group.id} data-testid={`group-${group.id}`}>
                <div
                  className="grid justify-center gap-x-4 gap-y-8 mx-auto"
                  style={{ gridTemplateColumns: `repeat(${ipr}, 96px)` }}
                >
                {group.links.map((link) => {
                  const isHttp = /^https?:/i.test(link.url);
                  const card = (
                    <a
                      key={link.id}
                      href={link.url}
                      {...(isHttp ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      onClick={(e) => handleClick(link, e)}
                      className="group flex flex-col items-center text-center w-24 link-card-bouncy"
                      data-testid={`link-card-${link.id}`}
                    >
                      <div className="link-card-tile-wrap">
                        <LogoDisplay
                          type={link.logo_type}
                          value={link.logo_value}
                          size={64}
                          alt={link.title}
                          tone={group.color || undefined}
                        />
                      </div>
                      <div className="mt-3 text-xs font-medium text-foreground line-clamp-2">
                        {link.title}
                      </div>
                    </a>
                  );
                  if (!link.description) {
                    return <React.Fragment key={link.id}>{card}</React.Fragment>;
                  }
                  return (
                    <Tooltip key={link.id} delayDuration={2000}>
                      <TooltipTrigger asChild>{card}</TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs text-center whitespace-pre-wrap" data-testid={`link-tooltip-${link.id}`}>
                        {link.description}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </section>
            );
          })}
        </div>
      </main>
      <footer className="border-t border-border py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-xs text-muted-foreground uppercase tracking-[0.2em]">
          Plateforme {site.title} — Accès rapide aux outils internes
        </div>
      </footer>
    </div>
    </TooltipProvider>
  );
}
